import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { X, Copy, Download, FileText, FileJson, FileCode, Table2, Code2, Braces, AlignLeft, FileSpreadsheet, Save } from 'lucide-react';
import {
  formatExport,
  formatExcel,
  triggerExcelDownload,
  triggerDownload,
  getFileExtension,
  getMimeType,
  type ExportColumn,
  type ExportFormat,
  type ExportOptions,
} from '@/utils/export';
import { useToastStore } from '@/stores/toast-store';
import { useDialogStore } from '@/stores/dialog-store';
import { useSettingsStore } from '@/stores/settings-store';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface ExportModalProps {
  columns: ExportColumn[];
  rows: Record<string, unknown>[];
  totalRows?: number;
  defaultTableName?: string;
  defaultFilename?: string;
  onClose: () => void;
}

interface FormatInfo {
  id: ExportFormat;
  label: string;
  icon: React.ElementType;
}

interface ExportPreset {
  id: string;
  name: string;
  format: ExportFormat;
  options: Record<string, unknown>;
}

// ═══════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════

const FORMATS: FormatInfo[] = [
  { id: 'csv', label: 'CSV', icon: FileText },
  { id: 'tsv', label: 'TSV', icon: FileText },
  { id: 'json', label: 'JSON', icon: FileJson },
  { id: 'sql', label: 'SQL', icon: FileCode },
  { id: 'markdown', label: 'Markdown', icon: Table2 },
  { id: 'html', label: 'HTML', icon: Code2 },
  { id: 'xml', label: 'XML', icon: Braces },
  { id: 'pretty', label: 'Pretty', icon: AlignLeft },
  { id: 'excel', label: 'Excel (.xlsx)', icon: FileSpreadsheet },
];

const PREVIEW_ROW_LIMIT = 50;

const inputClass =
  'w-full bg-dbgo-input border rounded-sm px-2 py-[5px] text-[12px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors';

const selectClass =
  'w-full bg-dbgo-input border rounded-sm px-2 py-[5px] text-[12px] text-text-primary outline-none focus:border-focus transition-colors appearance-none cursor-pointer';

const labelClass = 'block text-[11px] text-text-muted mb-1';

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function ExportModal({
  columns,
  rows,
  totalRows,
  defaultTableName,
  defaultFilename,
  onClose,
}: ExportModalProps) {
  const addToast = useToastStore((s) => s.addToast);
  const previewRef = useRef<HTMLPreElement>(null);
  const settingsSave = useSettingsStore((s) => s.save);

  // Presets
  const presetsJson = useSettingsStore((s) => s.get('export.presets', '[]'));
  const presets = useMemo<ExportPreset[]>(() => {
    try { return JSON.parse(presetsJson) as ExportPreset[]; } catch { return []; }
  }, [presetsJson]);

  // Format state
  const [format, setFormat] = useState<ExportFormat>('csv');

  // CSV/TSV options
  const [delimiter, setDelimiter] = useState(',');
  const [customDelimiter, setCustomDelimiter] = useState('');
  const [quoteChar, setQuoteChar] = useState('"');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [nullDisplay, setNullDisplay] = useState('');

  // JSON options
  const [jsonPretty, setJsonPretty] = useState(true);
  const [jsonStyle, setJsonStyle] = useState<'array' | 'jsonl'>('array');

  // SQL options
  const [sqlStyle, setSqlStyle] = useState<'insert' | 'insert-multi' | 'update' | 'upsert'>('insert');
  const [sqlTableName, setSqlTableName] = useState(defaultTableName ?? 'table_name');
  const [sqlIncludeDDL, setSqlIncludeDDL] = useState(false);
  const [sqlBatchSize, setSqlBatchSize] = useState(100);

  // HTML options
  const [htmlIncludeStyling, setHtmlIncludeStyling] = useState(true);

  // XML options
  const [xmlRootElement, setXmlRootElement] = useState('data');
  const [xmlRowElement, setXmlRowElement] = useState('row');
  const [xmlPretty, setXmlPretty] = useState(true);

  // Pretty options
  const [prettyBoxStyle, setPrettyBoxStyle] = useState<'ascii' | 'unicode' | 'simple'>('unicode');
  const [prettyMaxWidth, setPrettyMaxWidth] = useState(50);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Build export options from state
  const exportOptions = useMemo<ExportOptions>(() => {
    const effectiveDelimiter = format === 'tsv' ? '\t' : (delimiter === 'custom' ? customDelimiter : delimiter);
    return {
      format,
      delimiter: effectiveDelimiter,
      quoteChar: quoteChar === 'none' ? '' : quoteChar,
      includeHeaders,
      nullDisplay,
      jsonPretty,
      jsonStyle,
      sqlStyle,
      sqlTableName,
      sqlIncludeDDL,
      sqlBatchSize,
      htmlIncludeStyling,
      xmlRootElement,
      xmlRowElement,
      xmlPretty,
      prettyBoxStyle,
      prettyMaxWidth,
    };
  }, [
    format, delimiter, customDelimiter, quoteChar, includeHeaders, nullDisplay,
    jsonPretty, jsonStyle,
    sqlStyle, sqlTableName, sqlIncludeDDL, sqlBatchSize,
    htmlIncludeStyling,
    xmlRootElement, xmlRowElement, xmlPretty,
    prettyBoxStyle, prettyMaxWidth,
  ]);

  // Load a preset by applying its format + options
  const handleLoadPreset = useCallback((preset: ExportPreset) => {
    setFormat(preset.format);
    const o = preset.options;
    if (typeof o.delimiter === 'string') setDelimiter(o.delimiter);
    if (typeof o.customDelimiter === 'string') setCustomDelimiter(o.customDelimiter);
    if (typeof o.quoteChar === 'string') setQuoteChar(o.quoteChar);
    if (typeof o.includeHeaders === 'boolean') setIncludeHeaders(o.includeHeaders);
    if (typeof o.nullDisplay === 'string') setNullDisplay(o.nullDisplay);
    if (typeof o.jsonPretty === 'boolean') setJsonPretty(o.jsonPretty);
    if (o.jsonStyle === 'array' || o.jsonStyle === 'jsonl') setJsonStyle(o.jsonStyle);
    if (o.sqlStyle === 'insert' || o.sqlStyle === 'insert-multi' || o.sqlStyle === 'update' || o.sqlStyle === 'upsert') setSqlStyle(o.sqlStyle);
    if (typeof o.sqlTableName === 'string') setSqlTableName(o.sqlTableName);
    if (typeof o.sqlIncludeDDL === 'boolean') setSqlIncludeDDL(o.sqlIncludeDDL);
    if (typeof o.sqlBatchSize === 'number') setSqlBatchSize(o.sqlBatchSize);
    if (typeof o.htmlIncludeStyling === 'boolean') setHtmlIncludeStyling(o.htmlIncludeStyling);
    if (typeof o.xmlRootElement === 'string') setXmlRootElement(o.xmlRootElement);
    if (typeof o.xmlRowElement === 'string') setXmlRowElement(o.xmlRowElement);
    if (typeof o.xmlPretty === 'boolean') setXmlPretty(o.xmlPretty);
    if (o.prettyBoxStyle === 'ascii' || o.prettyBoxStyle === 'unicode' || o.prettyBoxStyle === 'simple') setPrettyBoxStyle(o.prettyBoxStyle);
    if (typeof o.prettyMaxWidth === 'number') setPrettyMaxWidth(o.prettyMaxWidth);
  }, []);

  // Save current settings as a preset
  const handleSavePreset = useCallback(async () => {
    const name = await useDialogStore.getState().showPrompt({
      title: 'Save Export Preset',
      placeholder: 'Preset name',
    });
    if (!name?.trim()) return;
    const newPreset: ExportPreset = {
      id: Date.now().toString(),
      name: name.trim(),
      format,
      options: {
        delimiter, customDelimiter, quoteChar, includeHeaders, nullDisplay,
        jsonPretty, jsonStyle,
        sqlStyle, sqlTableName, sqlIncludeDDL, sqlBatchSize,
        htmlIncludeStyling,
        xmlRootElement, xmlRowElement, xmlPretty,
        prettyBoxStyle, prettyMaxWidth,
      },
    };
    const updated = [...presets, newPreset];
    try {
      await settingsSave({ 'export.presets': JSON.stringify(updated) });
      addToast('success', `Preset "${name.trim()}" saved`);
    } catch {
      addToast('error', 'Failed to save preset');
    }
  }, [
    format, delimiter, customDelimiter, quoteChar, includeHeaders, nullDisplay,
    jsonPretty, jsonStyle, sqlStyle, sqlTableName, sqlIncludeDDL, sqlBatchSize,
    htmlIncludeStyling, xmlRootElement, xmlRowElement, xmlPretty,
    prettyBoxStyle, prettyMaxWidth, presets, settingsSave, addToast,
  ]);

  // Generate preview (first N rows)
  const preview = useMemo(() => {
    if (format === 'excel') {
      return 'Excel file will contain formatted spreadsheet with headers.\nBinary .xlsx format — no text preview available.';
    }
    const previewRows = rows.slice(0, PREVIEW_ROW_LIMIT);
    try {
      return formatExport(columns, previewRows, exportOptions);
    } catch {
      return '(Error generating preview)';
    }
  }, [columns, rows, exportOptions, format]);

  // Exporting state -- shows feedback for large datasets
  const [exporting, setExporting] = useState(false);

  // Generate full content for export (text-based formats only)
  const generateFullContent = useCallback(() => {
    return formatExport(columns, rows, exportOptions);
  }, [columns, rows, exportOptions]);

  const isExcel = format === 'excel';

  const handleCopyToClipboard = useCallback(async () => {
    if (isExcel) return; // Excel cannot be copied as text
    setExporting(true);
    try {
      // Yield to let the UI update with the "Exporting..." state
      await new Promise((resolve) => setTimeout(resolve, 50));
      const content = generateFullContent();
      await navigator.clipboard.writeText(content);
      addToast('success', 'Copied to clipboard');
    } catch {
      addToast('error', 'Failed to copy to clipboard');
    } finally {
      setExporting(false);
    }
  }, [isExcel, generateFullContent, addToast]);

  const handleExportToFile = useCallback(async () => {
    setExporting(true);
    try {
      // Yield to let the UI update with the "Exporting..." state
      await new Promise((resolve) => setTimeout(resolve, 50));
      const baseFilename = defaultFilename ?? 'export';
      if (isExcel) {
        const data = formatExcel(columns, rows);
        triggerExcelDownload(data, `${baseFilename}.xlsx`);
        addToast('success', `Exported as ${baseFilename}.xlsx`);
      } else {
        const content = generateFullContent();
        const ext = getFileExtension(format);
        const mime = getMimeType(format);
        const filename = baseFilename + ext;
        triggerDownload(content, filename, mime);
        addToast('success', `Exported as ${filename}`);
      }
    } finally {
      setExporting(false);
    }
  }, [isExcel, generateFullContent, format, defaultFilename, addToast, columns, rows]);

  const displayRowCount = rows.length;
  const displayTotalRows = totalRows ?? rows.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-[720px] h-[520px] flex flex-col bg-dbgo-modal border rounded-lg shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-sm font-semibold text-text-primary">Export Data</h2>
            {defaultTableName && (
              <span className="text-[11px] text-text-muted">
                Source: {defaultTableName} — {displayTotalRows.toLocaleString()} rows
                {displayRowCount < displayTotalRows && ` (showing ${displayRowCount.toLocaleString()})`}
              </span>
            )}
            {!defaultTableName && (
              <span className="text-[11px] text-text-muted">
                {displayRowCount.toLocaleString()} rows
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body: two panels */}
        <div className="flex flex-1 min-h-0 px-5 pb-3 gap-3">
          {/* Left panel: presets + format list + options */}
          <div className="w-[200px] flex-shrink-0 flex flex-col gap-2 overflow-y-auto">
            {/* Presets */}
            {presets.length > 0 && (
              <>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wide">Presets</span>
                  <select
                    className={selectClass}
                    defaultValue=""
                    onChange={(e) => {
                      const preset = presets.find((p) => p.id === e.target.value);
                      if (preset) handleLoadPreset(preset);
                      e.target.value = '';
                    }}
                  >
                    <option value="" disabled>Load a preset...</option>
                    {presets.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="border-t my-0.5" />
              </>
            )}

            {/* Format list */}
            <div className="flex flex-col gap-0.5">
              {FORMATS.map((f) => {
                const Icon = f.icon;
                const isSelected = format === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFormat(f.id)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-xs transition-colors text-left ${
                      isSelected
                        ? 'bg-accent-blue/15 text-accent-blue'
                        : 'text-text-secondary hover:bg-dbgo-tab-hover hover:text-text-primary'
                    }`}
                  >
                    <Icon size={13} />
                    {f.label}
                  </button>
                );
              })}
            </div>

            {/* Separator */}
            <div className="border-t my-1" />

            {/* Format-specific options */}
            <div className="flex flex-col gap-2 pb-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wide">Options</span>
                <button
                  type="button"
                  onClick={handleSavePreset}
                  title="Save current settings as preset"
                  className="flex items-center gap-1 text-[10px] text-text-muted hover:text-text-secondary transition-colors"
                >
                  <Save size={10} />
                  Save
                </button>
              </div>

              {(format === 'csv' || format === 'tsv') && (
                <CSVOptions
                  format={format}
                  delimiter={delimiter}
                  customDelimiter={customDelimiter}
                  quoteChar={quoteChar}
                  includeHeaders={includeHeaders}
                  nullDisplay={nullDisplay}
                  onDelimiterChange={setDelimiter}
                  onCustomDelimiterChange={setCustomDelimiter}
                  onQuoteCharChange={setQuoteChar}
                  onIncludeHeadersChange={setIncludeHeaders}
                  onNullDisplayChange={setNullDisplay}
                />
              )}

              {format === 'json' && (
                <JSONOptions
                  jsonPretty={jsonPretty}
                  jsonStyle={jsonStyle}
                  onPrettyChange={setJsonPretty}
                  onStyleChange={setJsonStyle}
                />
              )}

              {format === 'sql' && (
                <SQLOptions
                  sqlStyle={sqlStyle}
                  sqlTableName={sqlTableName}
                  sqlIncludeDDL={sqlIncludeDDL}
                  sqlBatchSize={sqlBatchSize}
                  onStyleChange={setSqlStyle}
                  onTableNameChange={setSqlTableName}
                  onIncludeDDLChange={setSqlIncludeDDL}
                  onBatchSizeChange={setSqlBatchSize}
                />
              )}

              {format === 'markdown' && (
                <div className="text-[11px] text-text-muted italic">No additional options</div>
              )}

              {format === 'html' && (
                <HTMLOptions
                  htmlIncludeStyling={htmlIncludeStyling}
                  onIncludeStylingChange={setHtmlIncludeStyling}
                />
              )}

              {format === 'xml' && (
                <XMLOptions
                  xmlRootElement={xmlRootElement}
                  xmlRowElement={xmlRowElement}
                  xmlPretty={xmlPretty}
                  onRootElementChange={setXmlRootElement}
                  onRowElementChange={setXmlRowElement}
                  onPrettyChange={setXmlPretty}
                />
              )}

              {format === 'pretty' && (
                <PrettyOptions
                  prettyBoxStyle={prettyBoxStyle}
                  prettyMaxWidth={prettyMaxWidth}
                  onBoxStyleChange={setPrettyBoxStyle}
                  onMaxWidthChange={setPrettyMaxWidth}
                />
              )}

              {format === 'excel' && (
                <div className="text-[11px] text-text-muted italic">
                  Exports as .xlsx with headers in the first row
                </div>
              )}
            </div>
          </div>

          {/* Right panel: preview */}
          <div className="flex-1 min-w-0 flex flex-col bg-dbgo-panel border rounded-md overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b bg-dbgo-panel">
              <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wide">Preview</span>
              <span className="text-[10px] text-text-muted">
                {rows.length > PREVIEW_ROW_LIMIT
                  ? `Showing ${PREVIEW_ROW_LIMIT} of ${rows.length.toLocaleString()} rows`
                  : `${rows.length.toLocaleString()} rows`}
              </span>
            </div>
            <pre
              ref={previewRef}
              className="flex-1 min-h-0 overflow-auto px-3 py-2 text-[11px] leading-[1.5] font-mono text-text-secondary whitespace-pre select-text"
            >
              {preview}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t">
          <span className="text-[11px] text-text-muted">
            {displayRowCount.toLocaleString()} row{displayRowCount !== 1 ? 's' : ''} will be exported
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyToClipboard}
              disabled={exporting || isExcel}
              title={isExcel ? 'Excel format cannot be copied as text' : undefined}
              className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-text-secondary border rounded-sm hover:text-text-primary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Copy size={12} />
              {exporting ? 'Exporting...' : 'Copy to Clipboard'}
            </button>
            <button
              type="button"
              onClick={handleExportToFile}
              disabled={exporting}
              className="flex items-center gap-1.5 px-4 py-[6px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={12} />
              {exporting ? 'Exporting...' : 'Export to File'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Option Sub-Components
// ═══════════════════════════════════════════

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-7 h-4 rounded-full transition-colors ${
          checked ? 'bg-accent-blue' : 'bg-dbgo-input border'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
            checked ? 'translate-x-3' : 'translate-x-0'
          }`}
        />
      </button>
      <span className="text-[11px] text-text-secondary">{label}</span>
    </label>
  );
}

// ── CSV/TSV Options ──

interface CSVOptionsProps {
  format: ExportFormat;
  delimiter: string;
  customDelimiter: string;
  quoteChar: string;
  includeHeaders: boolean;
  nullDisplay: string;
  onDelimiterChange: (v: string) => void;
  onCustomDelimiterChange: (v: string) => void;
  onQuoteCharChange: (v: string) => void;
  onIncludeHeadersChange: (v: boolean) => void;
  onNullDisplayChange: (v: string) => void;
}

function CSVOptions({
  format,
  delimiter,
  customDelimiter,
  quoteChar,
  includeHeaders,
  nullDisplay,
  onDelimiterChange,
  onCustomDelimiterChange,
  onQuoteCharChange,
  onIncludeHeadersChange,
  onNullDisplayChange,
}: CSVOptionsProps) {
  const isTSV = format === 'tsv';

  return (
    <>
      {!isTSV && (
        <div>
          <label className={labelClass}>Separator</label>
          <select
            value={delimiter}
            onChange={(e) => onDelimiterChange(e.target.value)}
            className={selectClass}
          >
            <option value=",">Comma (,)</option>
            <option value=";">Semicolon (;)</option>
            <option value="|">Pipe (|)</option>
            <option value="custom">Custom</option>
          </select>
          {delimiter === 'custom' && (
            <input
              type="text"
              value={customDelimiter}
              onChange={(e) => onCustomDelimiterChange(e.target.value)}
              placeholder="Enter separator"
              className={`${inputClass} mt-1`}
              maxLength={5}
            />
          )}
        </div>
      )}
      {isTSV && (
        <div className="text-[11px] text-text-muted italic">Separator: Tab (fixed)</div>
      )}
      <div>
        <label className={labelClass}>Quote Character</label>
        <select
          value={quoteChar}
          onChange={(e) => onQuoteCharChange(e.target.value)}
          className={selectClass}
        >
          <option value={'"'}>Double Quote (&quot;)</option>
          <option value="'">Single Quote (&apos;)</option>
          <option value="none">None</option>
        </select>
      </div>
      <Toggle checked={includeHeaders} onChange={onIncludeHeadersChange} label="Include headers" />
      <div>
        <label className={labelClass}>NULL display</label>
        <input
          type="text"
          value={nullDisplay}
          onChange={(e) => onNullDisplayChange(e.target.value)}
          placeholder="(empty)"
          className={inputClass}
        />
      </div>
    </>
  );
}

// ── JSON Options ──

interface JSONOptionsProps {
  jsonPretty: boolean;
  jsonStyle: 'array' | 'jsonl';
  onPrettyChange: (v: boolean) => void;
  onStyleChange: (v: 'array' | 'jsonl') => void;
}

function JSONOptions({ jsonPretty, jsonStyle, onPrettyChange, onStyleChange }: JSONOptionsProps) {
  return (
    <>
      <div>
        <label className={labelClass}>Style</label>
        <select
          value={jsonStyle}
          onChange={(e) => onStyleChange(e.target.value as 'array' | 'jsonl')}
          className={selectClass}
        >
          <option value="array">Array of Objects</option>
          <option value="jsonl">JSON Lines</option>
        </select>
      </div>
      <Toggle checked={jsonPretty} onChange={onPrettyChange} label="Pretty print" />
    </>
  );
}

// ── SQL Options ──

interface SQLOptionsProps {
  sqlStyle: 'insert' | 'insert-multi' | 'update' | 'upsert';
  sqlTableName: string;
  sqlIncludeDDL: boolean;
  sqlBatchSize: number;
  onStyleChange: (v: 'insert' | 'insert-multi' | 'update' | 'upsert') => void;
  onTableNameChange: (v: string) => void;
  onIncludeDDLChange: (v: boolean) => void;
  onBatchSizeChange: (v: number) => void;
}

function SQLOptions({
  sqlStyle,
  sqlTableName,
  sqlIncludeDDL,
  sqlBatchSize,
  onStyleChange,
  onTableNameChange,
  onIncludeDDLChange,
  onBatchSizeChange,
}: SQLOptionsProps) {
  return (
    <>
      <div>
        <label className={labelClass}>Style</label>
        <select
          value={sqlStyle}
          onChange={(e) => onStyleChange(e.target.value as 'insert' | 'insert-multi' | 'update' | 'upsert')}
          className={selectClass}
        >
          <option value="insert">INSERT (single)</option>
          <option value="insert-multi">INSERT (multi-row)</option>
          <option value="update">UPDATE</option>
          <option value="upsert">UPSERT (ON CONFLICT)</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>Table name</label>
        <input
          type="text"
          value={sqlTableName}
          onChange={(e) => onTableNameChange(e.target.value)}
          className={inputClass}
        />
      </div>
      <Toggle checked={sqlIncludeDDL} onChange={onIncludeDDLChange} label="Include DDL" />
      {sqlStyle === 'insert-multi' && (
        <div>
          <label className={labelClass}>Batch size</label>
          <input
            type="number"
            value={sqlBatchSize}
            onChange={(e) => onBatchSizeChange(parseInt(e.target.value, 10) || 100)}
            min={1}
            max={10000}
            className={inputClass}
          />
        </div>
      )}
    </>
  );
}

// ── HTML Options ──

interface HTMLOptionsProps {
  htmlIncludeStyling: boolean;
  onIncludeStylingChange: (v: boolean) => void;
}

function HTMLOptions({ htmlIncludeStyling, onIncludeStylingChange }: HTMLOptionsProps) {
  return (
    <Toggle checked={htmlIncludeStyling} onChange={onIncludeStylingChange} label="Include styling" />
  );
}

// ── XML Options ──

interface XMLOptionsProps {
  xmlRootElement: string;
  xmlRowElement: string;
  xmlPretty: boolean;
  onRootElementChange: (v: string) => void;
  onRowElementChange: (v: string) => void;
  onPrettyChange: (v: boolean) => void;
}

function XMLOptions({
  xmlRootElement,
  xmlRowElement,
  xmlPretty,
  onRootElementChange,
  onRowElementChange,
  onPrettyChange,
}: XMLOptionsProps) {
  return (
    <>
      <div>
        <label className={labelClass}>Root element</label>
        <input
          type="text"
          value={xmlRootElement}
          onChange={(e) => onRootElementChange(e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Row element</label>
        <input
          type="text"
          value={xmlRowElement}
          onChange={(e) => onRowElementChange(e.target.value)}
          className={inputClass}
        />
      </div>
      <Toggle checked={xmlPretty} onChange={onPrettyChange} label="Pretty print" />
    </>
  );
}

// ── Pretty Options ──

interface PrettyOptionsProps {
  prettyBoxStyle: 'ascii' | 'unicode' | 'simple';
  prettyMaxWidth: number;
  onBoxStyleChange: (v: 'ascii' | 'unicode' | 'simple') => void;
  onMaxWidthChange: (v: number) => void;
}

function PrettyOptions({
  prettyBoxStyle,
  prettyMaxWidth,
  onBoxStyleChange,
  onMaxWidthChange,
}: PrettyOptionsProps) {
  return (
    <>
      <div>
        <label className={labelClass}>Box style</label>
        <select
          value={prettyBoxStyle}
          onChange={(e) => onBoxStyleChange(e.target.value as 'ascii' | 'unicode' | 'simple')}
          className={selectClass}
        >
          <option value="unicode">Unicode</option>
          <option value="ascii">ASCII (+|-)</option>
          <option value="simple">Simple (spaces)</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>Max column width</label>
        <input
          type="number"
          value={prettyMaxWidth}
          onChange={(e) => onMaxWidthChange(parseInt(e.target.value, 10) || 50)}
          min={10}
          max={200}
          className={inputClass}
        />
      </div>
    </>
  );
}
